import { SUPPORTED_LANGUAGES } from '@/constants/constants';
import { PublicIcon } from '@/icons';
import { ClickAwayListener, Grow, Link, MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const LANG_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  pt: 'Português',
  fr: 'Français',
  tr: 'Türkçe',
  ru: 'Russian',
};

export function LangSelector() {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const nav = useNavigate();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLAnchorElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const restLangs = SUPPORTED_LANGUAGES.filter((v) => v !== i18n.language).sort((a, b) =>
    LANG_NAMES[a].localeCompare(LANG_NAMES[b]),
  );

  return (
    <>
      <Link ref={anchorRef} underline={'none'} onClick={handleToggle}>
        <Typography color={'#E2E8F0'} display={'flex'} alignItems={'center'} gap={2} fontWeight={600}>
          <PublicIcon />
          <span>{LANG_NAMES[i18n.language]}</span>
        </Typography>
      </Link>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        sx={{ borderRadius: 1.5, overflow: 'hidden' }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
            exit={false}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  id="language-menu"
                  onKeyDown={handleListKeyDown}
                  sx={(theme) => ({ minWidth: theme.spacing(37.5), color: theme.designTokens.slate[700] })}
                >
                  {restLangs.map((v) => (
                    <MenuItem
                      sx={{ fontWeight: 600 }}
                      key={v}
                      onClick={() => {
                        setOpen(false);
                        nav(
                          pathname
                            .split('/')
                            .map((val, i) => (i === 1 ? v : val))
                            .join('/'),
                        );
                      }}
                    >
                      {LANG_NAMES[v]}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
